import {
  coerceDate,
  coerceDateTime,
  formatIsoDate,
  formatIsoDateTimeItShort,
  formatMonthYear,
} from '@/utils/date';

const DAY_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_INSPECTION_TIME = '09:00';
export const DEFAULT_PLANNING_TIME = '09:00';
export const DEFAULT_PLANNING_TIMEZONE = 'Europe/Rome';
export const SCHEDULING_GRACE_DAYS = 21;

export type ScheduleState = 'draft' | 'planned' | 'overdue' | 'to_plan';

export type ScheduleDerivation = {
  anchorDate?: Date;
  expectedMonth?: Date;
  expectedWindowStart?: Date;
  expectedWindowEnd?: Date;
  dueMonthLabel?: string;
  isOverdue: boolean;
  state: ScheduleState;
};

export type ScheduleInputs = {
  contractActivationDate?: string | null;
  periodicityMonths?: number | null;
  lastFinalizedReportAt?: string | null;
  nextPlannedVisitAt?: string | null;
  hasDraft?: boolean;
  today?: Date;
};

type TimeParts = { hours: number; minutes: number };

export function parseTimeInput(value: string | null | undefined): TimeParts | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

export function combineDateAndTime(
  date: Date,
  time: string | null | undefined,
  fallbackTime: string = DEFAULT_PLANNING_TIME
): Date {
  const base = new Date(date.getTime());
  const timeParts = parseTimeInput(time) ?? parseTimeInput(fallbackTime);
  if (timeParts) {
    base.setHours(timeParts.hours, timeParts.minutes, 0, 0);
    return base;
  }
  base.setHours(0, 0, 0, 0);
  return base;
}

export function toIsoDateTime(value: Date): string {
  return value.toISOString();
}

export function applyDateToDateTime(
  dateIso: string | null | undefined,
  sourceDateTime: string | null | undefined,
  fallbackTime: string = DEFAULT_INSPECTION_TIME
): string | undefined {
  const date = coerceDate(dateIso);
  if (!date) return undefined;
  const source = coerceDateTime(sourceDateTime);
  const base = new Date(date.getTime());
  if (source) {
    base.setHours(source.getHours(), source.getMinutes(), 0, 0);
  } else {
    const combined = combineDateAndTime(base, fallbackTime, fallbackTime);
    return toIsoDateTime(combined);
  }
  return toIsoDateTime(base);
}

export function resolveLegacyDateToDateTime(
  dateIso: string | null | undefined,
  fallbackTime: string = DEFAULT_PLANNING_TIME
): string | undefined {
  const parsed = coerceDate(dateIso);
  if (!parsed) return undefined;
  const combined = combineDateAndTime(parsed, fallbackTime, fallbackTime);
  return toIsoDateTime(combined);
}

export function shouldAutoUpdateReportAt(input: {
  draftReportAt?: string | null;
  draftPlannedVisitAt?: string | null;
  oldPlannedVisitAt?: string | null;
  draftCreatedAt?: string | null;
  draftVisitDate?: string | null;
}): boolean {
  const reportAt = input.draftReportAt?.trim();
  if (!reportAt) return false;

  if (input.oldPlannedVisitAt && reportAt === input.oldPlannedVisitAt) {
    return true;
  }
  if (input.draftPlannedVisitAt && reportAt === input.draftPlannedVisitAt) {
    return true;
  }

  if (input.draftCreatedAt && input.draftVisitDate) {
    const createdAtDate = coerceDateTime(input.draftCreatedAt);
    if (createdAtDate) {
      const createdAtIso = createdAtDate.toISOString().slice(0, 10);
      return createdAtIso === input.draftVisitDate;
    }
  }

  return false;
}

export function shouldConsumePlannedVisit(input: {
  plannedVisitAt?: string | null;
  reportAt?: string | null;
  graceDays?: number;
}): boolean {
  const plannedDate = coerceDateTime(input.plannedVisitAt);
  const reportDate = coerceDateTime(input.reportAt);
  if (!plannedDate || !reportDate) return false;
  const graceDays = input.graceDays ?? SCHEDULING_GRACE_DAYS;
  const graceMs = graceDays * DAY_MS;
  return plannedDate.getTime() <= reportDate.getTime() + graceMs;
}

export function toIsoDateFromDateTime(dateTimeIso: string | null | undefined): string | undefined {
  const parsed = coerceDateTime(dateTimeIso);
  if (!parsed) return undefined;
  return formatIsoDate(parsed);
}

export function formatPlannedVisitLabel(
  plannedVisitAt: string | null | undefined,
  fallbackLabel: string
): string {
  if (!plannedVisitAt) return fallbackLabel;
  return formatIsoDateTimeItShort(plannedVisitAt, fallbackLabel);
}

export function deriveScheduleState({
  contractActivationDate,
  periodicityMonths,
  lastFinalizedReportAt,
  nextPlannedVisitAt,
  hasDraft = false,
  today = new Date(),
}: ScheduleInputs): ScheduleDerivation {
  const normalizedToday = startOfDay(today);
  const anchorDate =
    toDatePart(lastFinalizedReportAt) ?? toContractActivationDate(contractActivationDate);

  const expectedMonth =
    anchorDate && periodicityMonths
      ? startOfMonth(addMonths(anchorDate, periodicityMonths))
      : undefined;
  const expectedWindowStart = expectedMonth ? startOfMonth(expectedMonth) : undefined;
  const expectedWindowEnd = expectedMonth ? endOfMonth(expectedMonth) : undefined;
  const dueMonthLabel = expectedMonth
    ? formatMonthYear(
        `${expectedMonth.getFullYear()}-${String(expectedMonth.getMonth() + 1).padStart(
          2,
          '0'
        )}-01`,
        'it-IT'
      )
    : undefined;

  const isOverdue =
    !hasDraft &&
    !nextPlannedVisitAt &&
    !!expectedWindowEnd &&
    normalizedToday.getTime() > startOfDay(expectedWindowEnd).getTime();

  const state: ScheduleState = hasDraft
    ? 'draft'
    : nextPlannedVisitAt
      ? 'planned'
      : isOverdue
        ? 'overdue'
        : 'to_plan';

  return {
    anchorDate,
    expectedMonth,
    expectedWindowStart,
    expectedWindowEnd,
    dueMonthLabel,
    isOverdue,
    state,
  };
}

export function getReportScheduleWarnings(input: {
  anchorDate?: Date;
  expectedWindowStart?: Date;
  expectedWindowEnd?: Date;
  reportAt?: string | null;
  plannedVisitAt?: string | null;
  sitePlannedVisitAt?: string | null;
  graceDays?: number;
}): {
  hasWarning: boolean;
  isPlannedStale: boolean;
  isReportOutsideWindow: boolean;
  isPlannedMismatch: boolean;
} {
  const graceDays = input.graceDays ?? SCHEDULING_GRACE_DAYS;
  const reportDate = toDatePart(input.reportAt);
  const plannedDate = toDatePart(input.plannedVisitAt);
  const sitePlannedDate = toDatePart(input.sitePlannedVisitAt);
  const anchorDate = input.anchorDate ? startOfDay(input.anchorDate) : undefined;

  const isPlannedStale =
    !!sitePlannedDate && !!anchorDate && sitePlannedDate.getTime() <= anchorDate.getTime();

  const windowStart = input.expectedWindowStart
    ? shiftDays(input.expectedWindowStart, -graceDays)
    : undefined;
  const windowEnd = input.expectedWindowEnd
    ? shiftDays(input.expectedWindowEnd, graceDays)
    : undefined;
  const isReportOutsideWindow =
    !!reportDate &&
    !!windowStart &&
    !!windowEnd &&
    (reportDate.getTime() < startOfDay(windowStart).getTime() ||
      reportDate.getTime() > startOfDay(windowEnd).getTime());

  const plannedReportDiffDays =
    reportDate && plannedDate
      ? Math.abs(
          Math.round(
            (startOfDay(reportDate).getTime() - startOfDay(plannedDate).getTime()) / DAY_MS
          )
        )
      : null;

  const isPlannedMismatch = plannedReportDiffDays !== null && plannedReportDiffDays > graceDays;

  const hasWarning = isPlannedStale || isReportOutsideWindow || isPlannedMismatch;

  return {
    hasWarning,
    isPlannedStale,
    isReportOutsideWindow,
    isPlannedMismatch,
  };
}

export function resolvePlannedVisitAt(site: {
  periodicityNextVisitAt?: string | null;
  periodicityNextVisit?: string | null;
}): string | undefined {
  const explicit = site.periodicityNextVisitAt?.trim();
  if (explicit) return explicit;
  return resolveLegacyDateToDateTime(site.periodicityNextVisit, '00:00');
}

export function resolveReportAt(report: {
  reportAt?: string | null;
  meta?: { visitDate?: string | null } | null;
}): string | undefined {
  const explicit = report.reportAt?.trim();
  if (explicit) return explicit;
  return resolveLegacyDateToDateTime(report.meta?.visitDate ?? undefined, DEFAULT_INSPECTION_TIME);
}

function toDatePart(value: string | null | undefined): Date | undefined {
  const parsed = coerceDateTime(value);
  if (!parsed) return undefined;
  return startOfDay(parsed);
}

function toContractActivationDate(value: string | null | undefined): Date | undefined {
  const parsed = coerceDate(value);
  if (!parsed) return undefined;
  return startOfMonth(parsed);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), 1);
  result.setMonth(result.getMonth() + months);
  return result;
}

function shiftDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}
